"use client";

import { useGSAP, registerGsap, ScrollSmoother, ScrollTrigger } from "@/lib/motion/gsap";
import { prefersReducedMotion } from "@/lib/motion/reduced-motion";
import { buildSpine } from "@/lib/motion/spine";
import { bindScrollThrottle } from "@/lib/motion/scroll-throttle";
import {
  persistSpineSection,
  resolveActiveSection,
} from "@/lib/motion/section-deep-link";
import { useMotionBag } from "@/components/site/motion/MotionContext";

/** Page-level boot: ScrollSmoother, chrome, work slider, spine reset. Section timelines live on their components. */
export function PageBoot() {
  const { motion, arm } = useMotionBag();

  useGSAP(() => {
    /* Motion runtime is a mutable class instance (ADR-011). */
    /* eslint-disable react-hooks/immutability */
    registerGsap();
    motion.body = document.body;
    motion.header = document.querySelector("header");
    motion.isTouch =
      "ontouchstart" in window ||
      (navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints! > 0;
    if (motion.isTouch) motion.body.classList.add("isTouch");
    if (prefersReducedMotion()) motion.body.classList.add("reduced-motion");
    motion.body.classList.add("progress");
    motion.body.classList.add("splash-lock");
    motion.setVh();
    const onResizeVh = () => motion.setVh();
    window.addEventListener("resize", onResizeVh);
    /* Toolbar show/hide changes visualViewport without a width change — update
     * live visual layers only; runways stay on the width-gated `--vh`. */
    const onVisualViewport = () => motion.setVh2();
    window.visualViewport?.addEventListener("resize", onVisualViewport);
    window.visualViewport?.addEventListener("scroll", onVisualViewport);

    window.scroll(0, 0);
    motion.smoother = ScrollSmoother.create({
      wrapper: "#smooth-wrapper",
      content: "#smooth-content",
      smooth: prefersReducedMotion() ? 0 : 0.48,
      ignoreMobileResize: true,
      effects: true,
      smoothTouch: false,
      normalizeScroll: false,
    });
    bindScrollThrottle(motion);
    motion.lock(true);
    motion.bootChrome();
    /*
     * Defer THREE work-slider (large first-party JS) until after first paint /
     * idle so LCP is not blocked by WebGL boot. Spine uses m.slider?. safely.
     */
    const bootSlider = () => {
      if (motion.slider) return;
      void import("@/lib/motion/work-slider").then(
        ({ createWorkSlider, preloadSlides }) => {
          if (motion.slider) return;
          motion.slider = createWorkSlider("#container");
          if (motion.slider) {
            preloadSlides(motion.slider);
            motion.slider.resize();
          }
        },
      );
    };
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(bootSlider, { timeout: 1800 });
    } else {
      window.setTimeout(bootSlider, 400);
    }

    motion.spine = buildSpine(motion);
    motion.spine.bindOnce();
    motion.spine.logoLoops();
    // Match original `nt()`: spine reset/build runs immediately, not after fonts.
    motion.spine.reset();
    arm();

    const fonts = document.fonts?.ready;
    if (fonts) {
      void fonts.then(() => {
        requestAnimationFrame(() => {
          motion.spine?.rebuildSplits();
          ScrollTrigger.refresh();
          motion.inview();
        });
      });
    }

    /* Width change = full reload (ADR-018). Persist active section for post-boot restore. */
    let lastReloadWidth = window.innerWidth;
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    const onAppResize = () => {
      const width = window.innerWidth;
      if (Math.abs(width - lastReloadWidth) < 24) return;
      if (reloadTimer) clearTimeout(reloadTimer);
      reloadTimer = setTimeout(() => {
        if (Math.abs(window.innerWidth - lastReloadWidth) < 24) return;
        const section = resolveActiveSection(motion);
        if (section !== "hero") persistSpineSection(section);
        window.location.reload();
      }, 280);
    };
    window.addEventListener("resize", onAppResize);

    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      window.removeEventListener("resize", onResizeVh);
      window.removeEventListener("resize", onAppResize);
      window.visualViewport?.removeEventListener("resize", onVisualViewport);
      window.visualViewport?.removeEventListener("scroll", onVisualViewport);
      motion.destroy();
    };
  }, { dependencies: [] });

  return null;
}
